import React, { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Award, Clock, ShieldCheck, Zap } from 'lucide-react';

const slides = [
  {
    eyebrow: 'Express 2-Hour Delivery',
    title: 'Get Your SIM Card Delivered At Your Doorstep',
    description: 'Order a new SIM, port your existing number (MNP), or replace a lost SIM with instant biometric activation at home.',
    accent: 'from-orange-500/25 via-white/5 to-blue-500/20',
  },
  {
    eyebrow: 'Paperless Biometric e-KYC',
    title: 'Activate Your New Connection From Home',
    description: 'A verified delivery executive brings your SIM to the doorstep and completes secure activation in minutes.',
    accent: 'from-blue-500/25 via-white/5 to-cyan-500/20',
  },
  {
    eyebrow: 'Simple MNP Switching',
    title: 'Keep Your Number. Change Your Network.',
    description: 'Switch to Jio, Airtel, Vi, or BSNL without visiting a store or waiting in a queue.',
    accent: 'from-emerald-500/25 via-white/5 to-orange-500/20',
  },
];

export default function HeroCarousel() {
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveSlide((slide) => (slide + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  const slide = slides[activeSlide];

  return (
    <div className="relative min-h-[390px] overflow-hidden rounded-[2rem] border border-white/25 bg-white/5 p-8 text-white shadow-2xl shadow-slate-900/20 backdrop-blur-[2px] transition-colors duration-700 sm:p-12">
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${slide.accent}`} />
      <div className="relative z-10 max-w-3xl">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-300/30 bg-orange-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-orange-200">
          <Zap className="h-3.5 w-3.5" /> {slide.eyebrow}
        </div>
        <div key={activeSlide} className="animate-[fadeIn_500ms_ease-out]" aria-live="polite">
          <h1 className="mb-4 text-3xl font-black leading-tight tracking-tight sm:text-5xl">{slide.title}</h1>
          <p className="mb-8 text-base leading-relaxed text-slate-300 sm:text-lg">{slide.description}</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4 border-t border-white/15 pt-5 sm:grid-cols-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400 shrink-0" />
            <span className="text-xs font-medium text-slate-300">Fast Delivery</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-400 shrink-0" />
            <span className="text-xs font-medium text-slate-300">100% Safe e-KYC</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-400 shrink-0" />
            <span className="text-xs font-medium text-slate-300">Free Doorstep SIM</span>
          </div>
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-blue-400 shrink-0" />
            <span className="text-xs font-medium text-slate-300">Official Partner</span>
          </div>
        </div>
        <div className="mt-7 flex items-center justify-between">
          <div className="flex gap-2" aria-label="Hero slides">
            {slides.map((item, index) => (
              <button
                key={item.eyebrow}
                type="button"
                aria-label={`Show slide ${index + 1}`}
                onClick={() => setActiveSlide(index)}
                className={`h-1.5 rounded-full transition-all ${index === activeSlide ? 'w-8 bg-orange-400' : 'w-2 bg-white/30'}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button type="button" aria-label="Previous slide" onClick={() => setActiveSlide((activeSlide - 1 + slides.length) % slides.length)} className="rounded-full border border-white/20 bg-white/10 p-2 hover:bg-white/20"><ArrowLeft className="h-4 w-4" /></button>
            <button type="button" aria-label="Next slide" onClick={() => setActiveSlide((activeSlide + 1) % slides.length)} className="rounded-full border border-white/20 bg-white/10 p-2 hover:bg-white/20"><ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}